import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowUpRight, ExternalLink, RefreshCw } from 'lucide-react';
import api from '../../services/api';
import { DefinitionList, ErrorState, SectionCard, SpecGrid, StatusPill, useDelayedFlag } from '../../components/ui';
import { sbg } from '../../theme/sbgTokens';
import { formatDateTime, formatNumber, formatRelative } from '../../utils/format';

interface CalendarSettings {
  calendarEmail: string;
  syncFrequency: 'hourly' | 'daily' | 'manually';
  isEnabled: boolean;
}
interface SyncResult {
  processed?: number;
  created?: number;
  updated?: number;
  skipped?: number;
  syncTime?: string;
}
type Operation = 'save' | 'connect' | 'full' | 'recent';
type FeedbackScope = 'settings' | 'connection' | 'sync';
type Feedback = { scope: FeedbackScope; severity: 'success' | 'error' | 'info'; text: string };

const SCHEDULE_HELP: Record<CalendarSettings['syncFrequency'], string> = {
  hourly: 'Checks for updates every 5 minutes while the integration is enabled.',
  daily: 'Runs once per server calendar day.',
  manually: 'Run a sync below when you need to import changes.',
};

const SYNC_RESULTS: { key: keyof Omit<SyncResult, 'syncTime'>; label: string; caption: string }[] = [
  { key: 'processed', label: 'Processed', caption: 'Calendar events read' },
  { key: 'created', label: 'Created', caption: 'Time-off entries added' },
  { key: 'updated', label: 'Updated', caption: 'Time-off entries changed' },
  { key: 'skipped', label: 'Skipped', caption: 'Events not imported' },
];

const isValidTime = (value: string | null | undefined) => Boolean(value && Number.isFinite(new Date(value).getTime()));

// The form sets noValidate so errors show in the brand's own field style; this check replaces the
// browser's type="email" + required check, so a value like "OOO" is never saved as the calendar.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_ERROR = 'Enter the full calendar address, like OOO@sbgfunding.com.';

const progress = <CircularProgress size={16} color="inherit" aria-hidden="true" />;

/** Field-shaped skeletons at the form's real size, so the layout doesn't jump when settings arrive. */
const FieldSkeletons: React.FC<{ visible: boolean }> = ({ visible }) => (
  <Box aria-hidden="true" sx={{ display: 'grid', gap: 2.5, visibility: visible ? 'visible' : 'hidden' }}>
    {[0, 1].map((n) => (
      <Box key={n} sx={{ display: 'grid', gap: 0.75 }}>
        <Skeleton variant="rounded" width={140} height={16} />
        <Skeleton variant="rounded" height={36} />
        <Skeleton variant="rounded" width="60%" height={14} />
      </Box>
    ))}
    <Skeleton variant="rounded" width={220} height={20} />
  </Box>
);

const CalendarIntegration: React.FC = () => {
  const [settings, setSettings] = useState<CalendarSettings>({
    calendarEmail: 'OOO@sbgfunding.com', syncFrequency: 'hourly', isEnabled: true,
  });
  const [saved, setSaved] = useState<CalendarSettings | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [operation, setOperation] = useState<Operation | null>(null);
  const operationRef = useRef<Operation | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState<SyncResult | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const dirty = loadState === 'ready' && (saved === null || JSON.stringify(settings) !== JSON.stringify(saved));
  const busy = operation !== null || loadState !== 'ready';
  const showSkeletons = useDelayedFlag(loadState === 'loading', 300);

  useEffect(() => {
    let active = true;
    setLoadState('loading');
    api.get('/calendar/configure').then((response) => {
      const data = response.data.data;
      if (response.data.success === false || !data) throw new Error('Settings unavailable');
      if (!active) return;
      const current: CalendarSettings = {
        calendarEmail: data.calendarEmail,
        syncFrequency: data.syncFrequency,
        isEnabled: data.isEnabled,
      };
      setSettings(current);
      setSaved(data.configured ? current : null);
      setIsConnected(data.isConnected);
      setLastSyncedAt(data.lastSyncedAt);
      setLoadState('ready');
    }).catch(() => {
      if (active) setLoadState('error');
    });
    return () => { active = false; };
  }, [loadAttempt]);

  const begin = (next: Operation) => {
    if (operationRef.current || loadState !== 'ready' || (next !== 'save' && saved === null)) return false;
    operationRef.current = next;
    setOperation(next);
    setFeedback(null);
    return true;
  };
  const finish = () => { operationRef.current = null; setOperation(null); };

  const saveSettings = async (event: React.FormEvent) => {
    event.preventDefault();
    if (operationRef.current || loadState !== 'ready') return;
    if (!EMAIL_PATTERN.test(settings.calendarEmail.trim())) {
      setEmailError(EMAIL_ERROR);
      setFeedback(null);
      emailRef.current?.focus();
      return;
    }
    if (!begin('save')) return;
    const payload = { ...settings, calendarEmail: settings.calendarEmail.trim() };
    try {
      const response = await api.post('/calendar/configure', payload);
      if (response.data.success === false) throw new Error('Configuration failed');
      setSettings(payload);
      setSaved(payload);
      setFeedback({ scope: 'settings', severity: 'success', text: 'Calendar settings saved.' });
    } catch {
      setFeedback({
        scope: 'settings',
        severity: 'error',
        text: 'Calendar settings could not be saved. Check the shared OOO calendar address and try again; your changes are still here.',
      });
    } finally { finish(); }
  };

  const connect = async () => {
    if (!begin('connect')) return;
    // Open synchronously with the click so browsers do not block an async
    // popup. If popups are blocked, keep a direct link for the user instead.
    let loginWindow: Window | null = null;
    setAuthUrl(null);
    try {
      loginWindow = window.open('about:blank', '_blank');
      if (loginWindow) loginWindow.opener = null;
      const response = await api.get('/calendar/auth');
      const url = response.data.data?.authUrl;
      if (response.data.success === false || typeof url !== 'string' || !url.startsWith('https://')) {
        throw new Error('No authorization URL');
      }
      setAuthUrl(url);
      if (loginWindow) loginWindow.location.href = url;
      setFeedback({ scope: 'connection', severity: 'info', text: loginWindow
        ? 'Complete Microsoft sign-in in the new tab, then return here to sync.'
        : 'Your browser blocked the new tab. Continue to Microsoft using the link below to complete sign-in.' });
    } catch {
      loginWindow?.close();
      setFeedback({ scope: 'connection', severity: 'error', text: 'Microsoft sign-in could not be started. Try again, or check the integration setup with whoever manages it.' });
    } finally { finish(); }
  };

  const sync = async (kind: 'full' | 'recent') => {
    if (!begin(kind)) return;
    try {
      const response = kind === 'full'
        ? await api.post('/calendar/sync', { forceFullSync: true })
        : await api.post('/calendar/sync-incremental');
      if (response.data.success === false || !response.data.data) throw new Error('Sync failed');
      setLastSync(response.data.data);
      setLastSyncedAt(response.data.data.syncTime || null);
      setIsConnected(true);
      setFeedback({ scope: 'sync', severity: 'success', text: 'Sync finished. Review the results below, including any skipped events.' });
    } catch {
      setFeedback({ scope: 'sync', severity: 'error', text: 'Calendar sync could not be completed. Confirm Microsoft sign-in is complete, then try again.' });
    } finally { finish(); }
  };

  const retryLoad = () => setLoadAttempt((attempt) => attempt + 1);
  const hasSyncTime = isValidTime(lastSyncedAt);

  const feedbackFor = (scope: FeedbackScope) =>
    feedback && feedback.scope === scope ? (
      <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>
        {feedback.text}
      </Alert>
    ) : null;

  /* Status pills ------------------------------------------------------- */

  const settingsPill = loadState !== 'ready' ? null : !saved ? (
    <StatusPill tone="warning">Not configured</StatusPill>
  ) : dirty ? (
    <StatusPill tone="info">Unsaved changes</StatusPill>
  ) : (
    <StatusPill tone="success">Saved</StatusPill>
  );

  // `isConnected` only means Microsoft tokens are stored; the API doesn't report provider health,
  // so the pill says exactly that and nothing more.
  const connectionPill = loadState === 'ready' ? (
    isConnected ? (
      <StatusPill tone="success">Account linked</StatusPill>
    ) : (
      <StatusPill tone="warning">Account not linked</StatusPill>
    )
  ) : loadState === 'loading' ? (
    <Skeleton variant="rounded" width={88} height={22} sx={{ borderRadius: sbg.radius.pill, visibility: showSkeletons ? 'visible' : 'hidden' }} />
  ) : null;

  // Says why Save is disabled, or what saving will do.
  const settingsFooterNote = (() => {
    if (loadState === 'loading') return 'Settings are loading.';
    if (loadState === 'error') return 'Saving is off until the saved settings load.';
    if (!settings.calendarEmail.trim()) return 'Enter the shared calendar address to save.';
    if (!saved) return 'Save to finish setup. Nothing syncs until you do.';
    return dirty ? 'Changes apply to syncs after you save them.' : 'No changes to save.';
  })();

  /* Sync results ------------------------------------------------------- */

  let syncResults: React.ReactNode;
  if (lastSync) {
    syncResults = (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Typography variant="labelMd" component="p" sx={{ color: sbg.text.primary }}>
          Last completed sync{hasSyncTime ? ` · ${formatDateTime(lastSyncedAt)}` : ''}
        </Typography>
        <SpecGrid
          minColumnWidth={140}
          items={SYNC_RESULTS.map((item) => ({
            label: item.label,
            value: formatNumber(lastSync[item.key]),
            caption: item.caption,
          }))}
        />
      </Box>
    );
  } else if (loadState === 'loading') {
    syncResults = (
      <Box aria-hidden="true" sx={{ display: 'grid', gap: 1, py: 2, visibility: showSkeletons ? 'visible' : 'hidden' }}>
        <Skeleton variant="rounded" width="40%" height={16} />
        <Skeleton variant="rounded" width="65%" height={14} />
      </Box>
    );
  } else {
    // One quiet line, not an empty-state block: the last sync time already lives in the connection card.
    const line =
      loadState === 'error'
        ? 'Syncing is off until settings load.'
        : hasSyncTime
          ? 'Results from your next sync appear here.'
          : 'No sync has been recorded yet. Results from your first sync appear here.';
    syncResults = (
      <Typography variant="body2" sx={{ color: sbg.text.secondary }}>
        {line}
      </Typography>
    );
  }

  // Under the connect button: why it's off, or what to do next.
  const connectionNote = (() => {
    if (loadState === 'error') return 'Connecting is off until settings load.';
    if (loadState === 'ready' && !saved) return 'Save calendar settings first. Connecting uses the saved calendar address.';
    if (loadState === 'ready' && isConnected) return 'Reconnect if syncs start failing.';
    if (loadState === 'ready' && hasSyncTime) return 'Sign in again to resume syncing, then run a sync to confirm calendar access.';
    return 'After sign-in, run a sync to confirm calendar access.';
  })();

  return (
    <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
      {loadState === 'loading' && (
        <span className="sbg-sr-only" role="status">
          Loading calendar settings…
        </span>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          alignItems: 'stretch',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.4fr) minmax(320px, 1fr)' },
        }}
      >
        {/* Step 1: where to read from, and how often */}
        <SectionCard
          id="calendar-settings"
          title="Calendar settings"
          description="Which shared Outlook calendar attendance reads out-of-office events from, and how often it checks."
          action={settingsPill}
          footer={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', width: '100%' }}>
              <Typography variant="body2" sx={{ color: sbg.text.secondary }}>
                {settingsFooterNote}
              </Typography>
              <Button
                type="submit"
                form="calendar-settings-form"
                variant="contained"
                disabled={busy || !settings.calendarEmail.trim() || Boolean(saved && !dirty)}
                aria-busy={operation === 'save' || undefined}
                startIcon={operation === 'save' ? progress : undefined}
              >
                {operation === 'save' ? 'Saving…' : 'Save settings'}
              </Button>
            </Box>
          }
        >
          <Box sx={{ display: 'grid', gap: 2 }}>
            {feedbackFor('settings')}
            {loadState === 'loading' && <FieldSkeletons visible={showSkeletons} />}
            {loadState === 'error' && (
              <ErrorState
                compact
                title="Calendar settings could not be loaded"
                body="The server didn’t respond. Retry before making changes, so the saved settings aren’t replaced with defaults."
                onRetry={retryLoad}
                retryLabel="Retry"
              />
            )}
            {loadState === 'ready' && (
              <Box
                component="form"
                id="calendar-settings-form"
                noValidate
                onSubmit={saveSettings}
                aria-busy={operation === 'save'}
                sx={{ display: 'grid', gap: 2.5, maxWidth: 640 }}
              >
                {!saved && (
                  <Typography variant="body2" sx={{ color: sbg.text.secondary }}>
                    These are suggested values. Review them and save before connecting to Microsoft or syncing attendance.
                  </Typography>
                )}
                <TextField
                  label="Shared calendar email"
                  type="email"
                  required
                  fullWidth
                  value={settings.calendarEmail}
                  disabled={busy}
                  inputRef={emailRef}
                  onChange={(event) => {
                    setSettings({ ...settings, calendarEmail: event.target.value });
                    if (emailError) setEmailError(null);
                  }}
                  error={Boolean(emailError)}
                  helperText={emailError ?? 'Use the shared OOO calendar address.'}
                  slotProps={{ htmlInput: { spellCheck: false } }}
                />
                <FormControl fullWidth disabled={busy}>
                  <InputLabel id="calendar-sync-mode-label">Sync schedule</InputLabel>
                  <Select
                    labelId="calendar-sync-mode-label"
                    label="Sync schedule"
                    value={settings.syncFrequency}
                    onChange={(event) => setSettings({ ...settings, syncFrequency: event.target.value as CalendarSettings['syncFrequency'] })}
                  >
                    <MenuItem value="hourly">Automatic · every 5 minutes</MenuItem>
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="manually">Manual only</MenuItem>
                  </Select>
                  <FormHelperText>{SCHEDULE_HELP[settings.syncFrequency]}</FormHelperText>
                </FormControl>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={settings.isEnabled}
                      disabled={busy}
                      onChange={(_, checked) => setSettings({ ...settings, isEnabled: checked })}
                    />
                  )}
                  label="Enable automatic calendar sync"
                />
              </Box>
            )}
          </Box>
        </SectionCard>

        {/* Step 2: the Microsoft account that can read the calendar */}
        <SectionCard id="calendar-connection" title="Microsoft connection" action={connectionPill}>
          <Box sx={{ display: 'grid', gap: 2 }}>
            {feedbackFor('connection')}
            <DefinitionList
              items={[
                {
                  label: 'Calendar',
                  value: loadState === 'ready'
                    ? saved?.calendarEmail || <Box component="span" sx={{ color: sbg.text.secondary }}>Not saved yet</Box>
                    : '—',
                },
                {
                  label: 'Last sync',
                  value: loadState !== 'ready' ? '—' : hasSyncTime ? (
                    <Tooltip title={formatDateTime(lastSyncedAt)}>
                      <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatRelative(lastSyncedAt)}</Box>
                    </Tooltip>
                  ) : (
                    <Box component="span" sx={{ color: sbg.text.secondary }}>Never</Box>
                  ),
                },
              ]}
            />
            <Typography variant="body2" sx={{ color: sbg.text.secondary }}>
              Sign in with a Microsoft account that has access to the shared calendar. Microsoft may ask for administrator approval.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Button
                variant="outlined"
                onClick={connect}
                disabled={busy || !saved}
                aria-busy={operation === 'connect' || undefined}
                startIcon={operation === 'connect' ? progress : <ExternalLink />}
              >
                {operation === 'connect' ? 'Opening Microsoft…' : isConnected ? 'Reconnect Microsoft account' : 'Connect to Microsoft'}
              </Button>
              {authUrl && (
                <Button component="a" href={authUrl} target="_blank" rel="noopener noreferrer" variant="text" endIcon={<ArrowUpRight />}>
                  Continue to Microsoft
                </Button>
              )}
            </Box>
            <Typography variant="caption" component="p" sx={{ color: sbg.text.secondary }}>
              {connectionNote}
            </Typography>
          </Box>
        </SectionCard>
      </Box>

      {/* Step 3: import events into attendance */}
      <SectionCard id="calendar-sync" title="Sync attendance">
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ color: sbg.text.secondary, maxWidth: '64ch' }}>
              Imports calendar events using your saved settings and sends any related attendance notifications. Sync full
              year re-reads the whole year; Sync recent updates reads only what changed since the last sync.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Button
                variant="outlined"
                disabled={busy || !saved}
                onClick={() => sync('full')}
                aria-busy={operation === 'full' || undefined}
                startIcon={operation === 'full' ? progress : undefined}
              >
                {operation === 'full' ? 'Syncing year…' : 'Sync full year'}
              </Button>
              <Button
                variant="contained"
                disabled={busy || !saved}
                onClick={() => sync('recent')}
                aria-busy={operation === 'recent' || undefined}
                startIcon={operation === 'recent' ? progress : <RefreshCw />}
              >
                {operation === 'recent' ? 'Syncing updates…' : 'Sync recent updates'}
              </Button>
            </Box>
          </Box>
          {feedbackFor('sync')}
          {dirty && (
            <Alert severity="info" role="status">
              {saved
                ? 'Save your changes above to use them for the next sync.'
                : 'Save calendar settings above before connecting to Microsoft or syncing attendance.'}
            </Alert>
          )}
          <Box sx={{ borderTop: `1px solid ${sbg.border.subtle}`, pt: 2 }}>{syncResults}</Box>
        </Box>
      </SectionCard>
    </Box>
  );
};

export default CalendarIntegration;
